import { createListenerMiddleware } from '@reduxjs/toolkit';
import { monitoring } from 'common/services';
import { selectLocationSearchString, selectPathname } from 'modules/router/state/router.selectors';
import { natalieEventApi } from 'natalie/api/natalieEventApi';
import { type NatalieSource, resolveNatalieSource } from 'pages/Natalie/telemetry/natalieSource';
import {
  NATALIE_EVENT_ID_CONTEXT_KEY,
  NATALIE_SOURCE_CONTEXT_KEY,
  resolveNatalieEventId,
} from 'pages/Natalie/telemetry/natalieTelemetry';
import { getOrCreateVisitorId } from 'pages/Natalie/telemetry/visitorId';
import { LOCATION_CHANGE } from 'redux-first-history';
import { selectUserAccountId } from 'user/api/selectors';

import type { TypedStartListening } from '@reduxjs/toolkit';
import type { CombinedDispatch, RootState } from 'store';

type AppStartListening = TypedStartListening<RootState, CombinedDispatch>;

/**
 * Tags the Datadog RUM + Logs global context with `natalie_event_id` and
 * `natalie_source` while a participant is on a Natalie route, so all browser
 * telemetry (views, actions, resources, errors, session replay, forwarded logs)
 * is correlatable with the backend `natalie_event_id` and split by test vs real.
 *
 * Driven off `LOCATION_CHANGE` (redux-first-history) rather than a mounted
 * component: navigation is a Redux side effect, the pathname + search are
 * authoritative in `state.router`, and a single store-level listener covers both
 * the PreApp (pre-auth/accountless) and Dashboard (authed) route trees.
 * `natalie_source` is presence-based (`?testInviteCode=` → test), gated on the
 * same route set as the event-id tag.
 */
export const natalieTelemetryListener = createListenerMiddleware();
const startListening = natalieTelemetryListener.startListening as AppStartListening;

// Module-scoped current values so we only touch the SDK when the resolved id OR
// source actually changes. `natalie_event_id` is constant per event, but
// `natalie_source` can flip within an event (a step navigated without the
// query), so the dedup must key on BOTH — an early-return on eventId alone would
// swallow a same-event test→real transition.
let currentEventId: string | undefined;
let currentSource: NatalieSource | undefined;

// Events for which a visit has already been recorded this page load, so a single
// landing (and any same-event SPA re-navigation / test→real source flip) is one visit
// signal. Cleared naturally by a hard reload — matching "one visit per page load".
const emittedVisits = new Set<string>();

startListening({
  type: LOCATION_CHANGE,
  effect: (_action, { getState, dispatch }) => {
    const state = getState();
    const eventId = resolveNatalieEventId(selectPathname(state));
    // Gate the source read on the SAME condition as the event-id tag: only derive
    // a source while on a tagged Natalie participant route (eventId defined). A
    // stray `?testInviteCode=` on a `/natalie/` path whose id position is not a
    // real event id therefore never tags `natalie_source`.
    const source = eventId ? resolveNatalieSource(selectLocationSearchString(state)) : undefined;

    if (eventId === currentEventId && source === currentSource) return;
    currentEventId = eventId;
    currentSource = source;

    if (eventId) {
      monitoring.setContext(NATALIE_EVENT_ID_CONTEXT_KEY, eventId);

      // Conversion-funnel "visited" signal: fire-and-forget once per event per load.
      // Routed through the accountless natalie-api, which gates non-joinable events
      // (a harmless swallowed 400) — so authed/non-accountless routes produce no junk.
      if (!emittedVisits.has(eventId)) {
        emittedVisits.add(eventId);
        // accountId present only for authed visitors — lets the funnel attribute the visit
        // to a known user; accountless visitors send visitorId alone.
        const accountId = selectUserAccountId(state);
        void dispatch(
          natalieEventApi.endpoints.recordAccountlessVisit.initiate({
            eventId,
            visitorId: getOrCreateVisitorId(),
            ...(accountId ? { accountId: String(accountId) } : {}),
          })
        )
          .unwrap()
          .catch(() => {
            // analytics beacon — a failed visit must never affect the session
          });
      }
    } else {
      monitoring.removeContext(NATALIE_EVENT_ID_CONTEXT_KEY);
    }

    if (source) {
      monitoring.setContext(NATALIE_SOURCE_CONTEXT_KEY, source);
    } else {
      monitoring.removeContext(NATALIE_SOURCE_CONTEXT_KEY);
    }
  },
});

// `LOCATION_CHANGE` does not fire on a hard navigation / tab close, so the global
// `natalie_source` tag would otherwise stick on the context and mis-tag later
// telemetry. Register the clear ONCE at module init (not inside the effect, or a
// new listener would accumulate on every navigation).
if (typeof window !== 'undefined') {
  const clearSourceContext = () => monitoring.removeContext(NATALIE_SOURCE_CONTEXT_KEY);
  window.addEventListener('pagehide', clearSourceContext);
  window.addEventListener('beforeunload', clearSourceContext);
}
